import { useState } from "react";
import { datum, useEinsaetze, useEinsatzAnlegen, useEinsatzAendern, useEinsatzLoeschen, useEinsaetzeAusTerminen, useEinsatzFoto, useEinsatzFotoLoeschen, fotoUrl, nachweisUrl, EINSATZ_STATUS } from "../../hooks/useVertraege";
import type { VertragDetail, VertragLeistung, Einsatz, EinsatzFortschritt } from "../../hooks/useVertraege";
import { fehlertext } from "../../lib/fehler";

// ── Tab: Einsätze ───────────────────────────────────────────
/**
 * Die Einsatzakte — was tatsächlich geschehen ist.
 *
 * Der Kalender plant, der Vertrag verspricht, die Akte belegt. Ohne sie steht
 * im Streit Aussage gegen Aussage, und der Kunde bekommt keinen Nachweis für
 * § 35a EStG. Die Statuswahl ist deshalb nicht Kosmetik: „Zugang verwehrt"
 * gilt als erbracht, „fachlich entfallen" nicht — die Schlussabrechnung liest
 * genau das hier.
 */
export function TabEinsaetze({ v }: { v: VertragDetail }) {
  const { data, isLoading } = useEinsaetze(v.id);
  const ausTerminen = useEinsaetzeAusTerminen(v.id);
  const anlegen = useEinsatzAnlegen(v.id);
  const [offenForm, setOffenForm] = useState(false);
  const [meldung, setMeldung] = useState("");
  const [fehler, setFehler] = useState("");

  async function lauf(fn: () => Promise<unknown>, text: string) {
    setFehler(""); setMeldung("");
    try { await fn(); setMeldung(text); }
    catch (e) { setFehler(fehlertext(e) || "Fehlgeschlagen."); }
  }

  if (isLoading) return <div className="pf-card">Lade Einsätze…</div>;
  const akte = data;
  const nachzutragen = (akte?.offene_termine ?? []).filter(
    t => !t.abgesagt_am && new Date(t.end_at) < new Date()).length;

  return (
    <>
      <div className="pf-card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
          <div style={{ fontWeight: 700 }}>Einsatzakte</div>
          <button className="pf-btn is-ghost is-small" onClick={() => setOffenForm(o => !o)}>
            {offenForm ? "Abbrechen" : "＋ Einsatz"}
          </button>
        </div>
        <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", marginTop: 4, lineHeight: 1.5 }}>
          Was wann erledigt wurde. Grundlage für den Leistungsnachweis des Kunden
          (§ 35a EStG) und für die Schlussabrechnung.
        </div>

        {nachzutragen > 0 && (
          <div style={{
            marginTop: 12, padding: "10px 12px", borderRadius: 8,
            background: "var(--pf-amber-bg, #fdf6e3)", fontSize: 13, lineHeight: 1.5,
          }}>
            {nachzutragen === 1
              ? "Ein vergangener Termin hat noch keine Akte."
              : `${nachzutragen} vergangene Termine haben noch keine Akte.`}
            {" "}Der Regelfall ist, dass gefahren wurde wie geplant.
            <button className="pf-btn is-ghost is-small" style={{ marginTop: 8 }}
              disabled={ausTerminen.isPending}
              onClick={() => lauf(() => ausTerminen.mutateAsync(), "Akten angelegt — bitte durchsehen.")}>
              {ausTerminen.isPending ? "Lege an…" : "Aus Terminen anlegen"}
            </button>
          </div>
        )}

        {offenForm && (
          <EinsatzForm leistungen={v.leistungen}
            onSpeichern={async body => {
              await lauf(() => anlegen.mutateAsync(body), "Einsatz erfasst.");
              setOffenForm(false);
            }} />
        )}

        {meldung && <div style={{ marginTop: 10, fontSize: 13, color: "var(--pf-accent)" }}>{meldung}</div>}
        {fehler && <div style={{ marginTop: 10, fontSize: 13, color: "var(--pf-red)" }}>{fehler}</div>}

        {(akte?.einsaetze.length ?? 0) === 0 ? (
          <div className="pf-empty" style={{ marginTop: 12 }}>Noch kein Einsatz erfasst.</div>
        ) : (
          <div style={{ marginTop: 12 }}>
            {akte!.einsaetze.map(e => (
              <EinsatzZeile key={e.id} e={e} vertragId={v.id} leistungen={v.leistungen} />
            ))}
          </div>
        )}
      </div>

      <FortschrittKarte zeilen={akte?.fortschritt ?? []} />
      <NachweisKarte v={v} />
    </>
  );
}

/** Soll und Ist je Position. Die Zahl, die im Büro als Erstes gesucht wird:
 *  Was steht dieses Jahr noch aus? */
export function FortschrittKarte({ zeilen }: { zeilen: EinsatzFortschritt[] }) {
  if (!zeilen.length) return null;
  return (
    <div className="pf-card" style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 700 }}>Fortschritt im Vertragsjahr</div>
      <div style={{ marginTop: 10 }}>
        {zeilen.map(z => (
          <div key={z.leistung_id} style={{
            display: "flex", justifyContent: "space-between", gap: 10,
            padding: "7px 0", borderTop: "1px solid var(--pf-line)", fontSize: 14,
          }}>
            <div style={{ minWidth: 0 }}>{z.titel}</div>
            <div style={{ whiteSpace: "nowrap", color: z.offen === 0 ? "var(--pf-accent)" : "var(--pf-ink-2)" }}>
              {z.ist} / {z.soll}
              {z.entfallen > 0 && (
                <span style={{ color: "var(--pf-ink-3)" }}> · {z.entfallen} entfallen</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Der Nachweis für den Kunden. Auf Knopfdruck, nicht automatisch: ein
 *  Massenversand zur falschen Zeit ist schlimmer als ein Nachweis auf Zuruf. */
export function NachweisKarte({ v }: { v: VertragDetail }) {
  const jahr = new Date().getFullYear();
  return (
    <div className="pf-card" style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 700 }}>Leistungsnachweis</div>
      <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", marginTop: 4, lineHeight: 1.5 }}>
        Aufstellung der erbrachten Leistungen mit ausgewiesenem Lohnanteil. Der Kunde
        braucht sie für § 35a EStG — 20 % des Lohnanteils, höchstens 1.200 € im Jahr,
        zieht er direkt von seiner Steuerschuld ab.
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <a className="pf-btn is-ghost is-small" href={nachweisUrl(v.id)}
          target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
          Laufendes Vertragsjahr
        </a>
        <a className="pf-btn is-ghost is-small" href={nachweisUrl(v.id, jahr - 1)}
          target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
          Kalenderjahr {jahr - 1}
        </a>
        <a className="pf-btn is-ghost is-small" href={nachweisUrl(v.id, jahr)}
          target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
          Kalenderjahr {jahr}
        </a>
      </div>
    </div>
  );
}

export function EinsatzZeile({ e, vertragId, leistungen }: {
  e: Einsatz; vertragId: string; leistungen: VertragLeistung[];
}) {
  const aendern = useEinsatzAendern(vertragId);
  const loeschen = useEinsatzLoeschen(vertragId);
  const foto = useEinsatzFoto(vertragId);
  const fotoWeg = useEinsatzFotoLoeschen(vertragId);
  const [offen, setOffen] = useState(false);
  const [fehler, setFehler] = useState("");

  const label = EINSATZ_STATUS.find(s => s.wert === e.status)?.label ?? e.status;
  const erbracht = e.status === "durchgefuehrt" || e.status === "zugang_verwehrt";
  const titel = e.positionen.map(p => p.titel).filter(Boolean).join(", ");

  async function lauf(fn: () => Promise<unknown>) {
    setFehler("");
    try { await fn(); }
    catch (err) { setFehler(fehlertext(err) || "Fehlgeschlagen."); }
  }

  return (
    <div style={{ padding: "9px 0", borderTop: "1px solid var(--pf-line)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
        <button onClick={() => setOffen(o => !o)}
          style={{
            background: "none", border: "none", padding: 0, textAlign: "left",
            minWidth: 0, cursor: "pointer", font: "inherit", color: "inherit", flex: 1,
          }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{datum(e.datum)}</div>
          <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)" }}>
            {titel || "keine Position zugeordnet"}
          </div>
        </button>
        <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          <div style={{ fontSize: 13, color: erbracht ? "var(--pf-accent)" : "var(--pf-ink-3)" }}>
            {label}
          </div>
          {e.fotos.length > 0 && (
            <div style={{ fontSize: 12, color: "var(--pf-ink-3)" }}>
              {e.fotos.length} {e.fotos.length === 1 ? "Bild" : "Bilder"}
            </div>
          )}
        </div>
      </div>

      {offen && (
        <div style={{ marginTop: 10, paddingLeft: 2 }}>
          <label style={{ fontSize: 12.5, color: "var(--pf-ink-2)" }}>Status</label>
          <select className="pf-input" value={e.status}
            onChange={ev => lauf(() => aendern.mutateAsync({ eid: e.id, body: { status: ev.target.value } }))}>
            {EINSATZ_STATUS.map(s => <option key={s.wert} value={s.wert}>{s.label}</option>)}
          </select>

          {e.status === "zugang_verwehrt" && (
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 12.5, color: "var(--pf-ink-2)" }}>
                Ersparte Aufwendungen (€ netto)
              </label>
              <input className="pf-input" type="number" step="0.01" min="0"
                defaultValue={e.ersparte_aufwendungen}
                onBlur={ev => lauf(() => aendern.mutateAsync({
                  eid: e.id, body: { ersparte_aufwendungen: Number(ev.target.value) || 0 } }))} />
              <div style={{ fontSize: 12, color: "var(--pf-ink-3)", marginTop: 4, lineHeight: 1.5 }}>
                Der Einsatz gilt als erbracht (§ 5, Annahmeverzug) — abzüglich dessen, was
                nicht angefallen ist: Material, Entsorgung, Kraftstoff. Ohne diesen Abzug ist
                die Klausel angreifbar.
              </div>
            </div>
          )}

          <label style={{ fontSize: 12.5, color: "var(--pf-ink-2)", marginTop: 8, display: "block" }}>
            Erledigte Positionen
          </label>
          <div style={{ marginTop: 4 }}>
            {leistungen.filter(l => !l.optional || l.beauftragt).map(l => {
              const drin = e.positionen.some(p => p.leistung_id === l.id);
              return (
                <label key={l.id} style={{ display: "flex", gap: 8, alignItems: "center",
                  fontSize: 13.5, padding: "3px 0" }}>
                  <input type="checkbox" checked={drin} onChange={() => {
                    const neu = drin
                      ? e.positionen.filter(p => p.leistung_id !== l.id).map(p => p.leistung_id)
                      : [...e.positionen.map(p => p.leistung_id), l.id];
                    lauf(() => aendern.mutateAsync({ eid: e.id, body: { positionen: neu } }));
                  }} />
                  <span>{l.titel_override || l.katalog_titel || "Leistung"}</span>
                </label>
              );
            })}
          </div>

          <label style={{ fontSize: 12.5, color: "var(--pf-ink-2)", marginTop: 8, display: "block" }}>
            Notiz
          </label>
          <textarea className="pf-input" rows={2} defaultValue={e.notiz}
            onBlur={ev => lauf(() => aendern.mutateAsync({ eid: e.id, body: { notiz: ev.target.value } }))} />

          {e.fotos.length > 0 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
              {e.fotos.map(f => (
                <div key={f.id} style={{ width: 96 }}>
                  <a href={fotoUrl(e.id, f.id)} target="_blank" rel="noopener noreferrer">
                    <img src={fotoUrl(e.id, f.id)} alt={f.art}
                      style={{ width: 96, height: 72, objectFit: "cover", borderRadius: 6 }} />
                  </a>
                  <button className="pf-btn is-ghost is-small"
                    style={{ color: "var(--pf-red)", marginTop: 2 }}
                    onClick={() => lauf(() => fotoWeg.mutateAsync({ eid: e.id, fid: f.id }))}>
                    entfernen
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            {(["vorher", "nachher", "doku"] as const).map(art => (
              <label key={art} className="pf-btn is-ghost is-small" style={{ cursor: "pointer" }}>
                ＋ {art === "doku" ? "Bild" : art}
                <input type="file" accept="image/*" style={{ display: "none" }}
                  onChange={ev => {
                    const d = ev.target.files?.[0];
                    if (d) lauf(() => foto.mutateAsync({ eid: e.id, art, datei: d }));
                    ev.target.value = "";
                  }} />
              </label>
            ))}
            <button className="pf-btn is-ghost is-small" style={{ color: "var(--pf-red)" }}
              onClick={() => lauf(() => loeschen.mutateAsync(e.id))}>
              Einsatz löschen
            </button>
          </div>

          {fehler && <div style={{ marginTop: 8, fontSize: 13, color: "var(--pf-red)" }}>{fehler}</div>}
        </div>
      )}
    </div>
  );
}

export function EinsatzForm({ leistungen, onSpeichern }: {
  leistungen: VertragLeistung[];
  onSpeichern: (body: { datum: string; status: string; positionen: string[]; notiz: string }) => void;
}) {
  const [datumWert, setDatumWert] = useState(new Date().toISOString().slice(0, 10));
  const [status, setStatus] = useState("durchgefuehrt");
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [notiz, setNotiz] = useState("");
  const auswahl = leistungen.filter(l => !l.optional || l.beauftragt);

  return (
    <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: "var(--pf-bg-2, #f6f7f3)" }}>
      <label style={{ fontSize: 12.5, color: "var(--pf-ink-2)" }}>Datum</label>
      <input className="pf-input" type="date" value={datumWert}
        onChange={e => setDatumWert(e.target.value)} />

      <label style={{ fontSize: 12.5, color: "var(--pf-ink-2)", marginTop: 8, display: "block" }}>
        Status
      </label>
      <select className="pf-input" value={status} onChange={e => setStatus(e.target.value)}>
        {EINSATZ_STATUS.map(s => <option key={s.wert} value={s.wert}>{s.label}</option>)}
      </select>

      <label style={{ fontSize: 12.5, color: "var(--pf-ink-2)", marginTop: 8, display: "block" }}>
        Erledigte Positionen
      </label>
      <div>
        {auswahl.map(l => (
          <label key={l.id} style={{ display: "flex", gap: 8, alignItems: "center",
            fontSize: 13.5, padding: "3px 0" }}>
            <input type="checkbox" checked={gewaehlt.includes(l.id)}
              onChange={() => setGewaehlt(g =>
                g.includes(l.id) ? g.filter(x => x !== l.id) : [...g, l.id])} />
            <span>{l.titel_override || l.katalog_titel || "Leistung"}</span>
          </label>
        ))}
      </div>

      <label style={{ fontSize: 12.5, color: "var(--pf-ink-2)", marginTop: 8, display: "block" }}>
        Notiz
      </label>
      <textarea className="pf-input" rows={2} value={notiz} onChange={e => setNotiz(e.target.value)} />

      <button className="pf-btn" style={{ marginTop: 10 }}
        onClick={() => onSpeichern({ datum: datumWert, status, positionen: gewaehlt, notiz })}>
        Einsatz speichern
      </button>
    </div>
  );
}

